import { useCallback, useEffect, useRef } from 'react';
import RichTextEditor from '../../RichTextEditor';
import styled from 'styled-components';
import { IRichTextEditorRef } from '../../RichTextEditor/RichTextEditor';
import useAnalysisSaveSummaryRichText from '../../../utils/hooks/useAnalysisSaveSummaryRichText';
import { IconName, IconWrapper, SvgIcon } from '../../Icon';
import { useChartContext } from '../ChartContext';
import ActionsMenu from '../../ActionsMenu';
import { SingleSelectOption } from '../../Dropdown/SingleSelect';
import { theme } from '../../../styles/theme';
import ScrollableWrapper from '../../ScrollableWrapper';
import { assertCondition } from '../../../utils/types';

interface IProps {
  analysisId: string;
  inDashboard?: boolean;
}

const RichTextEditorWrapper = styled(ScrollableWrapper)`
  max-height: 210px;
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  column-gap: 4px;
  padding: 12px 8px;
  .tiptap {
    height: auto;
    padding: 0 4px 0 0;
  }
`;

const ChartSummaryRichText = ({ analysisId, inDashboard }: IProps) => {
  const editorRef = useRef<IRichTextEditorRef>(null);

  const chartContext = useChartContext();
  assertCondition(chartContext.__typename === 'BaseChartContext');
  const {
    metadata: { richTextSummary },
    permissions: { entityPermissions },
  } = chartContext;
  const {
    saveSummaryRichText,
    deleteSummaryRichText,
    setShowSummaryInDashboard,
    saving,
  } = useAnalysisSaveSummaryRichText({
    analysisId,
  });

  const hasPermission = entityPermissions.editAnalysis;
  const editorDisabled = !hasPermission || saving;

  const handleDeleteSummary = useCallback(async () => {
    if (editorDisabled) {
      return;
    }
    await deleteSummaryRichText();
  }, [deleteSummaryRichText, editorDisabled]);

  const toggleShowInDashboard = useCallback(async () => {
    if (editorDisabled) {
      return;
    }
    const newShowInDashboard = richTextSummary
      ? !richTextSummary.showInDashboard
      : true;
    await setShowSummaryInDashboard(newShowInDashboard);
  }, [editorDisabled, richTextSummary, setShowSummaryInDashboard]);

  const toggleShowInDashboardIcon: IconName = richTextSummary?.showInDashboard
    ? 'eye-slash'
    : 'eye';
  const toggleShowInDashboardLabel = richTextSummary?.showInDashboard
    ? 'Hide from Dashboard view'
    : 'Show in Dashboard view';

  // XXX this is a bit of a hack, react to the html changing outside the editor
  // which right now can only happen when "Save to Report" in the Explain With AI panel.
  // The alternative to this would be to move the editorRef state somewhere near the top
  // of the page (has to be shared with the Builder Right Side Nav)
  useEffect(() => {
    editorRef.current?.reset(richTextSummary?.richText.html);
  }, [richTextSummary?.richText.html]);

  if (!richTextSummary || (inDashboard && !richTextSummary.showInDashboard)) {
    return null;
  }

  return (
    <Wrapper>
      <IconWrapper size={24}>
        <SvgIcon
          name="lumos"
          width={16}
          height={16}
          customFill={theme.colors.purple500}
        />
      </IconWrapper>
      <RichTextEditorWrapper>
        <RichTextEditor
          ref={editorRef}
          disabled={editorDisabled}
          value={richTextSummary.richText.html}
          onChange={saveSummaryRichText}
        />
      </RichTextEditorWrapper>
      <ActionsMenu>
        <SingleSelectOption
          iconName="trash"
          onClick={handleDeleteSummary}
          label={'Delete Summary'}
          disabled={editorDisabled}
        />
        <SingleSelectOption
          iconName={toggleShowInDashboardIcon}
          onClick={toggleShowInDashboard}
          label={toggleShowInDashboardLabel}
          disabled={editorDisabled}
        />
      </ActionsMenu>
    </Wrapper>
  );
};

export default ChartSummaryRichText;
